'use strict';

const throttle = require('./throttle');
const { isInViewport } = require('./measures');

/**
 * Lazy content loading.
 * It needs a valid marker inside the page to operate.
 * It can operate on all sorts of pages.
 */
const LazyContentLoader = {

    init() {
        this.config = this.getConfiguration();
        this.currentRequest = null;

        this.positionUpdateHandler = throttle(this.handlePositionUpdate.bind(this), 100);
        this.dataHandler = this.handleData.bind(this);

        if (this.config) {
            this.setListeners(true);
            this.handlePositionUpdate();
        }
    },

    dispose() {
        this.setListeners(false);
        this.config = null;
    },

    handlePositionUpdate() {
        if (!this.config) {
            return;
        }

        const config = this.config;

        if (config.immediate) {
            this.performRequest();
            return;
        }

        const inViewPort = isInViewport(config.marker, config.margin);

        if (inViewPort) {
            this.performRequest();
        }
    },

    performRequest() {
        if (this.currentRequest) {
            return;
        }

        try {
            this.currentRequest = new XMLHttpRequest();
            this.currentRequest.onreadystatechange = this.dataHandler;
            this.currentRequest.open('GET', this.config.url, true);
            this.currentRequest.send();
        } catch (e) {
            this.currentRequest = null;
            this.dispose();
        }
    },

    handleData() {
        const request = this.currentRequest;

        if (!request || request.readyState !== 4) {
            return;
        }

        this.currentRequest = null;

        // If request was not successful, we do not try again.
        if (request.status !== 200) {
            this.dispose();

            return;
        }

        this.addData(request);
    },

    addData(request) {
        // Early exit when dispose was called.
        if (!this.config) {
            return;
        }

        // Replace marker with new content.
        $(this.config.marker).replaceWith(request.responseText);

        // Inform listeners about new content added.
        setTimeout(() => {
            document.documentElement.dispatchEvent(
                new CustomEvent('content:updated', {
                    bubbles: true
                })
            );
        }, 25);

        // Check if new content contains a new marker.
        this.config = this.getConfiguration();

        // If not, we stop listening.
        if (!this.config) {
            this.dispose();
        }
    },

    getConfiguration() {
        const marker = document.querySelector('.js-insert-marker');

        if (!marker) {
            return null;
        }

        const url = marker.getAttribute('data-lazyload-url') || '';

        if (!url) {
            return null;
        }

        let margin = marker.getAttribute('data-viewport-margin');
        margin = margin && /^\d+$/.test(margin) ? parseInt(margin, 10) : 400;

        const immediate = marker.getAttribute('data-immmediate') === 'true';

        return {
            marker,
            url,
            margin,
            immediate
        };
    },

    setListeners(activate) {
        const method = activate ? 'addEventListener' : 'removeEventListener';

        window[method]('resize', this.positionUpdateHandler);
        window[method]('scroll', this.positionUpdateHandler);
    }

};

/**
 * Public.
 */
module.exports = LazyContentLoader;
